import { createOrderV4 as createOrderAPI } from '@develop/ia-api-library'
import { defineStore } from 'pinia'
import type { CheckoutOrderModel } from '@/models/checkout-order-model'

export const useOrderStore = defineStore('order', () => {
  const authStore = useAuthStore()
  const checkoutStore = useCheckoutStore()
  const userStore = useUserStore()

  const anonymousUser = useAnonymousUser()
  const { locale } = useTranslation()

  const DEFAULT_ORDER: CheckoutOrderModel = {
    isSubscribedToNewsletter: false,
    orderCode: null,
    preOrderCode: null,
    isWhatsAppPreferredContact: false,
  }

  const orderData = ref<CheckoutOrderModel>(DEFAULT_ORDER)

  const isLoading = ref(false)

  const anonymousUserId = computed(() => authStore.token ? undefined : anonymousUser.getToken())

  const setIsSubscribedToNewsletter = (value: boolean) =>
    orderData.value = { ...orderData.value, isSubscribedToNewsletter: value }

  const setIsWhatsAppPreferredContact = (value: boolean) =>
    orderData.value = { ...orderData.value, isWhatsAppPreferredContact: value }

  const setOrderCode = (orderCode: string) =>
    orderData.value = { ...orderData.value, orderCode }

  const setPreOrderCode = (preOrderCode: string) =>
    orderData.value = { ...orderData.value, preOrderCode }

  const createOrder = async (paypalOrderId?: string) => {
    const { trackingHeaders } = useDeviceInfo()
    if (!checkoutStore.draftId) {
      onShowLogs(() => console.log('Draft ID is missing.'))
      return false
    }
    const { token, refreshToken } = authStore

    const options = {
      token: token ?? '',
      refreshToken: refreshToken ?? '',
      language: locale,
      trackingHeaders,
    }
    const payload = {
      anonymousUserId: anonymousUserId.value,
      subscribeToNewsletter: orderData.value.isSubscribedToNewsletter,
      paypalOrderId,
      preferredContactWhatsappForGuest: userStore.userId ? undefined : orderData.value.isWhatsAppPreferredContact,
      // TODO: Investigate legacy FEC what exactly to send here, until then send null
      utm: null,
    }

    set(isLoading, true)

    const { success, data } = await createOrderAPI(payload, checkoutStore.draftId, options)
    set(isLoading, false)

    if (success && data) {
      // How can we have multiple orders at once?
      const { isPreorder, orderCode } = data.orders[0]
      if (isPreorder && orderCode)
        setPreOrderCode(orderCode)
      else if (orderCode)
        setOrderCode(orderCode)
    }

    return success
  }

  const reset = () => set(orderData, DEFAULT_ORDER)

  return {
    orderData,
    isLoading,
    setIsWhatsAppPreferredContact,
    setIsSubscribedToNewsletter,
    createOrder,
    reset,
  }
})
